/**
 * TierPreviewSection — Read-only tier table with computed example prices
 *
 * Shows: base_price * (1 - discount_%) for each tier, plus perk badges.
 * If no base price available, shows a message.
 *
 * Part of Plan 199, Epic 02.
 */

import { Badge } from "@/components/ui/badge";
import type { TierRow } from "./PromotionTierTable";

interface Props {
  tiers: TierRow[];
  basePrice: number | null;
}

export function TierPreviewSection({ tiers, basePrice }: Props) {
  const validTiers = tiers.filter(
    (t) => t.min_qty && t.discount_percentage
  );

  if (validTiers.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Add tier levels above to see a price preview.
      </p>
    );
  }

  if (basePrice === null || basePrice <= 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Link an item to see computed prices.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-2 min-w-0">
        {validTiers.map((tier) => {
          const discount = parseFloat(tier.discount_percentage) || 0;
          const computedPrice = basePrice * (1 - discount / 100);
          const qtyLabel = tier.max_qty
            ? `${tier.min_qty}-${tier.max_qty} pcs`
            : `${tier.min_qty}+ pcs`;

          return (
            <div
              key={tier.localId}
              className="flex-1 min-w-[120px] rounded-xl bg-muted/50 p-3 text-center space-y-1"
            >
              <p className="text-xs font-medium text-muted-foreground">
                {qtyLabel}
              </p>
              <p className="text-lg font-semibold tabular-nums">
                ฿{formatPrice(computedPrice)}
              </p>
              <p className="text-xs text-green-600">{discount}% off</p>
              {(tier.perk_names || []).length > 0 && (
                <div className="flex flex-wrap gap-1 justify-center mt-1">
                  {tier.perk_names.map((perk) => (
                    <Badge
                      key={perk}
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 h-4 bg-green-50 text-green-700 border-green-200"
                    >
                      + {perk}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatPrice(value: number): string {
  return value.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}
